import { useController, type FieldValues } from 'react-hook-form';
import { Form } from 'semantic-ui-react';

import DatePicker, { type DatePickerProps } from '@components/date-picker';

import { FieldError } from './field-error';
import type { FieldProps } from './field.types';

export type FieldDateProps<T extends FieldValues = FieldValues> = FieldProps<
  T,
  DatePickerProps,
  'selected' | 'onChange' | 'value'
>;

/**
 * Date picker field wired to React Hook Form via `useController`.
 *
 * All additional props are forwarded to the underlying `DatePicker`.
 */
export const FieldDate = <T extends FieldValues>({
  className,
  control,
  label,
  name,
  required,
  width,
  ...rest
}: FieldDateProps<T>) => {
  const { field, fieldState } = useController({ control, name });
  const id = rest.id ?? name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      {label && <label htmlFor={id}>{label}</label>}

      <DatePicker
        {...rest}
        value={field.value}
        onChange={date => field.onChange(date)}
        buttonProps={{ size: 'tiny' }}
      />

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
